import {StyleSheet} from "aphrodite";
import * as React from "react";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import {View} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";

type Props = {
    /**
     * The custom color passed to the active tab.
     */
    activeTabColor: string;
    /**
     * The active tab identifier.
     */
    activeTab: string;
    /**
     * The list of tabs to render.
     */
    tabs: Array<{
        id: string;
        label: string;
        testId: string | null | undefined;
    }>;
    /**
     * Called when a tab is selected.
     */
    onTabChange: (id: string) => void;
};

type DefaultProps = {
    activeTabColor: Props["activeTabColor"];
};

/**
 * Tab Filter allows to select any of the filters associated to a table and uses
 * that info to execute a particular action. This action can be filtering out
 * the results of the current table or navigating to a different page. A
 * selection is always required.
 */
class FilterTabs extends React.Component<Props> {
    static defaultProps: DefaultProps = {
        activeTabColor: color.blue,
    };

    render(): React.ReactNode {
        const {activeTab, tabs, activeTabColor} = this.props;
        return (
            <View role="tablist" style={styles.tabList}>
                {tabs.map((tab) => {
                    const selected = tab.id === activeTab;
                    return (
                        <Clickable
                            key={tab.id}
                            hideDefaultFocusRing={true}
                            onClick={() => this.props.onTabChange(tab.id)}
                            aria-selected={selected ? "true" : "false"}
                            role="tab"
                            testId={tab.testId || ""}
                        >
                            {({hovered, focused, pressed}) => (
                                <View
                                    style={[
                                        styles.tab,
                                        hovered && styles.hovered,
                                        focused && styles.focused,
                                        pressed && styles.pressed,
                                    ]}
                                >
                                    <LabelMedium
                                        style={[
                                            styles.tabLabel,
                                            selected && {
                                                color: activeTabColor,
                                                borderBottomColor:
                                                    activeTabColor,
                                            },
                                        ]}
                                    >
                                        {tab.label}
                                    </LabelMedium>
                                </View>
                            )}
                        </Clickable>
                    );
                })}
            </View>
        );
    }
}

const styles = StyleSheet.create({
    tabList: {
        flexDirection: "row",
    },
    tab: {
        borderRadius: spacing.xxxxSmall_2,
        padding: `0 ${spacing.xSmall_8}px`,
    },
    focused: {
        boxShadow: `0 0 0 ${spacing.xxxxSmall_2}px ${color.blue}`,
    },
    hovered: {
        backgroundColor: color.fadedBlue16,
    },
    pressed: {
        backgroundColor: color.fadedBlue,
    },
    tabLabel: {
        borderBottom: "3px solid transparent",
        color: "inherit",
        fontWeight: 700,
        padding: `${spacing.xSmall_8}px 0`,
    },
});

export default FilterTabs;
